import { useState } from "react";
import { UploadCloud, Loader2, X } from "lucide-react";
import { adminApi, imageUrl } from "../../lib/adminApi";

const empty = {
  title: "",
  location: "",
  category: { fr: "", nl: "" },
  description: { fr: "", nl: "" },
  image_id: null,
  published: true,
  featured: false,
};

const field = "w-full rounded-xl bg-ink border border-white/12 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-accent-yellow focus:outline-none transition-colors";
const lbl = "block text-xs uppercase tracking-[0.2em] text-white/45 mb-2";

export const ProjectForm = ({ initial, onCancel, onSaved }) => {
  const [form, setForm] = useState(initial ? { ...empty, ...initial } : empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLang = (k, l, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [l]: v } }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const { image_id } = await adminApi.uploadImage(file);
      set("image_id", image_id);
    } catch (err) {
      setError("Échec de l'envoi de l'image.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      setError("Le titre est obligatoire.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: form.title,
        location: form.location,
        category: form.category,
        description: form.description,
        image_id: form.image_id,
        published: form.published,
        featured: form.featured,
      };
      if (initial?.id) await adminApi.updateProject(initial.id, payload);
      else await adminApi.createProject(payload);
      onSaved();
    } catch (err) {
      setError(err.message || "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/12 bg-white/[0.03] p-6 md:p-8" data-testid="project-form">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-xl font-medium">{initial?.id ? "Modifier le projet" : "Nouveau projet"}</h3>
        <button onClick={onCancel} data-testid="project-form-close" className="text-white/50 hover:text-white transition-colors"><X size={20} /></button>
      </div>

      <div className="grid gap-5">
        <div>
          <label className={lbl}>Titre *</label>
          <input className={field} data-testid="project-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Villa contemporaine" />
        </div>
        <div>
          <label className={lbl}>Localisation</label>
          <input className={field} data-testid="project-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Bruxelles" />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={lbl}>Catégorie (FR)</label>
            <input className={field} data-testid="project-category-fr" value={form.category.fr} onChange={(e) => setLang("category", "fr", e.target.value)} placeholder="Fenêtres & portes" />
          </div>
          <div>
            <label className={lbl}>Catégorie (NL)</label>
            <input className={field} data-testid="project-category-nl" value={form.category.nl} onChange={(e) => setLang("category", "nl", e.target.value)} placeholder="Ramen & deuren" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={lbl}>Description (FR)</label>
            <textarea rows={3} className={field} data-testid="project-desc-fr" value={form.description.fr} onChange={(e) => setLang("description", "fr", e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Description (NL)</label>
            <textarea rows={3} className={field} data-testid="project-desc-nl" value={form.description.nl} onChange={(e) => setLang("description", "nl", e.target.value)} />
          </div>
        </div>

        <div>
          <label className={lbl}>Image</label>
          <div className="flex items-center gap-5">
            <div className="w-28 h-20 rounded-lg overflow-hidden bg-white/[0.05] border border-white/10 shrink-0 flex items-center justify-center">
              {form.image_id ? (
                <img src={imageUrl(form.image_id)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[10px] text-white/30">Aucune</span>
              )}
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/20 text-sm cursor-pointer hover:border-accent-yellow hover:text-accent-yellow transition-colors" data-testid="project-image-upload">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
              {uploading ? "Envoi…" : "Choisir une image"}
              <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} />
            </label>
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="inline-flex items-center gap-2.5 text-sm text-white/70 cursor-pointer" data-testid="project-published-toggle">
            <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="accent-[color:var(--accent-yellow,#FFCC00)] w-4 h-4" />
            Publié (visible sur le site)
          </label>
          <label className="inline-flex items-center gap-2.5 text-sm text-white/70 cursor-pointer" data-testid="project-featured-toggle">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[color:var(--accent-yellow,#FFCC00)] w-4 h-4" />
            Mis en avant (page d'accueil)
          </label>
        </div>

        {error && <p className="text-sm text-red-400" data-testid="project-form-error">{error}</p>}

        <div className="flex gap-3 pt-1">
          <button onClick={save} disabled={saving} data-testid="project-save" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent-yellow text-ink text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
            {saving && <Loader2 size={16} className="animate-spin" />}
            Enregistrer
          </button>
          <button onClick={onCancel} data-testid="project-cancel" className="px-6 py-3 rounded-xl border border-white/20 text-sm hover:border-white/40 transition-colors">Annuler</button>
        </div>
      </div>
    </div>
  );
};
